import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createSaleReport,
  getDashboardDailyWaste,
  getDashboardRouteLive,
  getListSaleReport,
  getSaleDepositReport,
} from "../../services";
import { Dayjs } from "dayjs";
import {
  DashboardDailyWaste,
  DashboardRouteLive,
} from "../../types/dashboard.type";
import { SaleRouteRequest } from "../../types/sale-route.type";
import { SalesReport } from "../../types/sale-reports.type";
import { SaleDepositResponse } from "../../types/sale-deposit.type";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { featureAccess } from "../../permissions/access";
// เงินใช้ formatNumber (2 ตำแหน่ง) ส่วนจำนวนชิ้นใช้ numberWithCommas (จำนวนเต็ม)
import { formatNumber, numberWithCommas } from "../../utils";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import WasteTableSkeleton from "./Skeleton/WasteTableSkeleton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NetOfNotesHint from "../SaleDepositReport/NetOfNotesHint";
import TransferAmountText from "../SaleDepositReport/TransferAmountText";
import TransferDiffText from "../SaleDepositReport/TransferDiffText";
import DepositNoteModal from "../SaleDepositReport/DepositNoteModal";
import TransferNotificationModal, {
  TransferNotificationTarget,
} from "../SaleDepositReport/TransferNotificationModal";
import TransferSlipsModal from "../SaleDepositReport/TransferSlipsModal";
import RouteLiveStockModal from "./RouteLiveStockModal";
import {
  DepositReviewPatch,
  useDepositActions,
} from "../SaleDepositReport/useDepositActions";
import ReviewCheckboxCell from "../SaleDepositReport/ReviewCheckboxCell";
import {
  CASH_IN_HAND_HINT,
  CASH_IN_HAND_LABEL,
  formatCashInHand,
  normalizeTransferSlips,
  physicalCash as physicalCashOf,
} from "../SaleDepositReport/transfer.utils";

interface RouteTableProps {
  selectedDate: Dayjs | null;
  triggerFetch: number;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

const STATUS_COLOR: Record<string, string> = {
  "in progress": "#5C69E8",
  pending: "#5C69E8",
  submitted: "#FDA42C",
  "finance approved": "#FDA42C",
  "account approved": "#be458c",
  approved: "#45BE77",
};

const STATUS_LABEL: Record<string, string> = {
  "in progress": "ยังไม่ได้เพิ่มข้อมูล",
  pending: "ยังไม่ได้เพิ่มข้อมูล",
  submitted: "รอการเงินตรวจสอบ",
  "finance approved": "รอบัญชีตรวจสอบ",
  "account approved": "รอ HO ตรวจสอบ",
  approved: "HO ตรวจสอบแล้ว",
};

/** สายที่ถูกเลือกเพื่อเปิด modal สต็อกสด */
interface SelectedRoute {
  route_uuid: string;
  route_code: string;
}

// Matches the muted header treatment the other columns in this table already use.
const depositHeaderCellSx = { color: "text.disabled", fontWeight: 600 } as const;

export default function RouteTable({
  selectedDate,
  triggerFetch,
  setTriggerFetch,
}: RouteTableProps) {
  const navigate = useNavigate();
  const role = useUserRole();
  const { isHeadOfficeAccounting } = role;
  const showDepositColumns = featureAccess.saleDepositColumns(role);
  const canEditSlipAmount = featureAccess.saleDepositEditAmount(role);

  const [dailyWaste, setDailyWaste] = useState<DashboardDailyWaste[]>();
  const [routeLive, setRouteLive] = useState<DashboardRouteLive[]>([]);
  const [reports, setReports] = useState<SalesReport[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  // Keyed by uuid rather than a snapshot of the slips, so a corrected amount shows up as
  // soon as the deposits refetch.
  const [slipTargetUuid, setSlipTargetUuid] = useState<string | null>(null);
  const [deposits, setDeposits] = useState<SaleDepositResponse[]>([]);
  const [noteTarget, setNoteTarget] = useState<SaleDepositResponse | null>(null);
  const [notificationTarget, setNotificationTarget] =
    useState<TransferNotificationTarget | null>(null);
  const [stockTarget, setStockTarget] = useState<SelectedRoute | null>(null);

  // ลำดับคำขอล่าสุดของตาราง ใช้ทิ้งผลลัพธ์ที่มาช้ากว่า (ดู loadRouteData)
  const latestRequestRef = useRef(0);

  // การโอนยอด (แจ้งยอด/สลิป/ตรวจแล้ว/หมายเหตุ) อ่านจาก sale-deposit โดยตรง ไม่ผ่าน
  // sales_report อีกต่อไป — BO จึงแจ้งยอดโอนได้ทันทีที่มีเช็คอิน โดยไม่ต้องรอ checker
  // กระทบยอดหรือรอสร้างรายงาน
  const depositsByRoute = useMemo(
    () => new Map(deposits.map((deposit) => [deposit.route_code, deposit])),
    [deposits],
  );

  const slipTarget = useMemo(
    () => deposits.find((deposit) => deposit.uuid === slipTargetUuid) ?? null,
    [deposits, slipTargetUuid],
  );
  const slipItems = useMemo(
    () => (slipTarget ? normalizeTransferSlips(slipTarget) : []),
    [slipTarget],
  );

  // ยอดสด: #ร้านค้า / เงินเชื่อ / เงินสด / สต็อก / ของเสีย — ไม่ผูกกับ sales_report
  // จึงมีค่าตั้งแต่ระหว่างวัน ไม่ต้องรอ reconcile หรือการสร้างรายงาน
  const liveByRoute = useMemo(
    () => new Map(routeLive.map((live) => [live.route_code, live])),
    [routeLive],
  );

  const setDepositReview = useCallback(
    (depositUuid: string, patch: DepositReviewPatch) => {
      setDeposits((prev) =>
        prev.map((deposit) =>
          deposit.uuid === depositUuid ? { ...deposit, ...patch } : deposit,
        ),
      );
    },
    [],
  );

  const setDepositNote = useCallback(
    (depositUuid: string, note: string | null) => {
      setDeposits((prev) =>
        prev.map((deposit) =>
          deposit.uuid === depositUuid ? { ...deposit, note } : deposit,
        ),
      );
    },
    [],
  );

  const { reviewLoading, toggleReview, noteSaving, saveNote } =
    useDepositActions(setDepositReview, setDepositNote);

  // BO's own sign-off. Head office signs the same row off on its dedicated report, but the
  // two ticks are independent: the branch may still correct its own after head office signs.
  const changeDepositReviewed =
    (deposit: SaleDepositResponse | undefined) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!deposit) return;
      toggleReview(deposit.uuid, "bo", event.target.checked);
    };

  const handleSaveNote = async (note: string) => {
    if (!noteTarget) return;
    const saved = await saveNote(noteTarget.uuid, note.trim() ? note : null);
    if (saved) setNoteTarget(null);
  };

  const loadRouteData = useCallback(async (date: Dayjs) => {
    const formatted = date.format("YYYY-MM-DD");
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    const isStale = () => latestRequestRef.current !== requestId;

    setLoading(true);
    try {
      // daily-waste เป็นเจ้าของ "รายการแถว" + สถานะ, route-live เติมเฉพาะตัวเลขสด
      // และ sale-deposit เติมคอลัมน์การโอนยอด — ยิงขนานกันเพราะไม่ขึ้นต่อกัน
      const [wasteResponse, liveResponse, depositResponse, reportResponse] =
        await Promise.all([
          getDashboardDailyWaste(formatted),
          getDashboardRouteLive(formatted),
          getSaleDepositReport(1, 99, {
            dateFrom: formatted,
            dateTo: formatted,
          }),
          getListSaleReport(1, 99, formatted),
        ]);
      if (isStale()) return;
      setDailyWaste(wasteResponse.data.data);
      setRouteLive(liveResponse.data.data ?? []);
      setDeposits(depositResponse.data.data?.rows ?? []);
      setReports(reportResponse.data.data.rows);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      loadRouteData(selectedDate);
    }
  }, [triggerFetch, selectedDate, loadRouteData]);

  const handleCreateReport = async (route: string) => {
    if (isHeadOfficeAccounting) return;

    setLoading(true);
    try {
      // Create the report for the date the dashboard is viewing, not "today",
      // so backdated rows (selectedDate in the past) reconcile with BE data.
      const payload: SaleRouteRequest = { route };
      if (selectedDate?.isValid()) {
        payload.report_date = selectedDate.format("YYYY-MM-DD");
      }

      const response = await createSaleReport(payload);
      setTriggerFetch((prev) => prev + 1);
      navigate(`/report/sale/details/${response.data?.data?.uuid}`);
    } catch {
      // Refetch on failure too. A create can fail at the gateway (timeout) after
      // the server already wrote the report; without this refresh the new row
      // stays absent from `reports`, checkIsDisabled keeps the button enabled,
      // and the user's retry click creates a second report for the same route.
      // The error itself is already surfaced by the axios interceptor.
      setTriggerFetch((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const checkIsDisabled = useCallback(
    (status: string, routeCode: string) => {
      const isCreatedReport = reports.some(
        (report) => report.route === routeCode,
      );

      return !(status === "submitted" && !isCreatedReport);
    },
    [reports],
  );

  if (!dailyWaste) return <WasteTableSkeleton />;

  return (
    <Box sx={{ position: "relative" }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            bgcolor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={40} thickness={5} />
        </Box>
      )}
      <Paper
        sx={{
          padding: 0,
          maxHeight: "623px",
          overflow: dailyWaste.length > 0 ? "auto" : "hidden",
        }}
      >
        <Typography
          sx={{ padding: "16px 16px 0" }}
          variant="body1"
          mb={2}
          fontWeight={600}
        >
          ข้อมูลสายส่ง
        </Typography>
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                bgcolor: "#f4f4f4",
                "& td, & th": { padding: "8px" },
              }}
            >
              <TableRow>
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600 }}
                >
                  ลำดับ
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600 }}
                >
                  สายส่ง
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600 }}
                >
                  #ร้านค้า
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600 }}
                >
                  Checker
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600 }}
                >
                  เงินเชื่อ
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  <Tooltip title={CASH_IN_HAND_HINT}>
                    <Box
                      component="span"
                      sx={{ borderBottom: "1px dotted", cursor: "help" }}
                    >
                      {CASH_IN_HAND_LABEL}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600 }}
                >
                  เงินโอน
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: "#aeaeae",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  รวมเงินสด
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: "#aeaeae",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  คงเหลือบนรถ
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: "#aeaeae",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  ของเสียเก็บกลับ
                </TableCell>
                {showDepositColumns && (
                  <>
                    <TableCell
                      align="center"
                      sx={{ color: "#aeaeae", fontWeight: 600 }}
                    >
                      ค่าใช้จ่าย
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#aeaeae",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      ต้องโอน/ยอดโอน
                    </TableCell>
                    <TableCell align="center" sx={depositHeaderCellSx}>
                      ผลต่าง
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ color: "#aeaeae", fontWeight: 600 }}
                    >
                      สลิป
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ color: "#aeaeae", fontWeight: 600 }}
                    >
                      ตรวจแล้ว (BO)
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ color: "#aeaeae", fontWeight: 600 }}
                    >
                      หมายเหตุ
                    </TableCell>
                  </>
                )}
                <TableCell
                  align="center"
                  sx={{ color: "#aeaeae", fontWeight: 600 }}
                >
                  สถานะสายส่ง
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dailyWaste.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={12 + (showDepositColumns ? 6 : 0)}
                    align="center"
                    sx={{
                      py: 5,
                    }}
                  >
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่พบข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {dailyWaste.length > 0 &&
                dailyWaste.map((waste, index) => {
                  const live = liveByRoute.get(waste.route_code);
                  const deposit = depositsByRoute.get(waste.route_code);
                  // Money here is the CASH-HANDLING basis (VAT-inclusive), the same figures
                  // รายงานการโอน shows — these columns sit beside ต้องโอน/ยอดโอน, which is real
                  // money moved. The sale report's frozen totals are deliberately NOT read:
                  // they are EX-VAT for reporting, so the box used to show ~7% less than the
                  // remit next to it and เงินสด − ค่าใช้จ่าย never matched ที่ต้องโอน.
                  //
                  // Once BO has notified, the deposit's own frozen figures win, so the two
                  // screens cannot disagree; before that it is the live count.
                  const creditTotal =
                    deposit?.credit_total_value ?? live?.credit_total_value;
                  const cashTotal =
                    deposit?.cash_total_value ?? live?.cash_total_value;
                  const transferTotal =
                    deposit?.transfer_total_value ?? live?.transfer_total_value ?? 0;
                  const physicalCash =
                    cashTotal == null
                      ? null
                      : physicalCashOf({
                          cash_total_value: cashTotal,
                          transfer_total_value: transferTotal,
                          physical_cash_total_value: deposit
                            ? null
                            : live?.physical_cash_total_value,
                        });
                  return (
                    <TableRow
                      key={waste.uuid}
                      sx={{
                        "& td, & th": {
                          borderBottom: "none",
                          padding: "8px",
                        },
                      }}
                    >
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="ดูสต็อกบนรถ">
                        <IconButton
                          onClick={() =>
                            setStockTarget({
                              route_uuid: waste.route_uuid,
                              route_code: waste.route_code,
                            })
                          }
                          sx={{
                            bgcolor: (theme) =>
                              theme.palette.primary.main + "1A",
                            mr: 1,
                            "&:hover": {
                              bgcolor: (theme) =>
                                theme.palette.primary.main + "33",
                            },
                          }}
                        >
                          <AirportShuttleOutlinedIcon
                            sx={{
                              fontSize: 16,
                              color: (theme) => theme.palette.primary.main,
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      {waste.route_code}
                    </TableCell>
                    <TableCell align="center">
                      {live?.customer_count ?? "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={500}>
                        {waste.reconciled_by_name || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {creditTotal == null ? "-" : formatNumber(creditTotal)}
                    </TableCell>
                    <TableCell align="center">
                      {physicalCash == null ? "-" : formatCashInHand(physicalCash)}
                    </TableCell>
                    <TableCell align="center">
                      {cashTotal == null ? "-" : formatNumber(transferTotal)}
                    </TableCell>
                    <TableCell align="center">
                      {cashTotal == null ? "-" : formatNumber(cashTotal)}
                    </TableCell>
                    <TableCell align="center">
                      {live?.route_stock_remaining_qty == null
                        ? "-"
                        : numberWithCommas(live.route_stock_remaining_qty)}
                    </TableCell>
                    <TableCell align="center">
                      {live?.waste_collected_qty == null
                        ? "-"
                        : numberWithCommas(live.waste_collected_qty)}
                    </TableCell>
                    {showDepositColumns && (
                      <>
                        <TableCell align="center">
                          {deposit?.total_expense_amount == null
                            ? "-"
                            : formatNumber(deposit.total_expense_amount)}
                        </TableCell>
                        <TableCell align="center">
                          {deposit && deposit.transfer_amount != null ? (
                            // มีการโอนจริงแล้ว (มีสลิป) → ล็อกเป็นตัวเลข ต้องโอน/ยอดโอน
                            <TransferAmountText
                              required={deposit.required_transfer_amount}
                              transfer={deposit.transfer_amount}
                            />
                          ) : waste.checkin_uuid ? (
                            // ยังไม่มีการโอนจริง → ยังกดแจ้งยอดโอนได้ (สร้างใหม่ หรือแก้ไข
                            // รายการเดิมที่แจ้งไว้แต่ยังไม่ได้โอน) พร้อมใช้ตั้งแต่มีเช็คอิน
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="center"
                              gap={0.25}
                            >
                              {deposit ? (
                                // แจ้งไปแล้วแต่ยังไม่ได้สลิป: ยอดที่แจ้งไว้คือข้อมูลหลัก
                                // ส่วนการแก้ไขย่อเหลือไอคอนดินสอ (ชื่อปุ่มอยู่ใน tooltip)
                                <>
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatNumber(
                                      deposit.required_transfer_amount,
                                    )}
                                  </Typography>
                                  <Tooltip title="แก้ไขยอดโอน">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        setNotificationTarget({
                                          checkinUuid:
                                            waste.checkin_uuid as string,
                                          route: waste.route_code,
                                        })
                                      }
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    setNotificationTarget({
                                      checkinUuid: waste.checkin_uuid as string,
                                      route: waste.route_code,
                                    })
                                  }
                                  sx={{
                                    minWidth: 0,
                                    p: 0,
                                    color: "link.main",
                                    textDecoration: "underline",
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                      textDecoration: "underline",
                                      bgcolor: "transparent",
                                    },
                                  }}
                                >
                                  แจ้งยอดโอน
                                </Button>
                              )}
                            </Stack>
                          ) : (
                            "-"
                          )}
                          <NetOfNotesHint
                            adjustment={deposit?.credit_debit_note_adjustment}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TransferDiffText
                            required={deposit?.required_transfer_amount}
                            transfer={deposit?.transfer_amount}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {deposit &&
                          ((deposit.slips?.length ?? 0) > 0 ||
                            deposit.slip_url) ? (
                            <Tooltip title="ดูสลิป">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setSlipTargetUuid(deposit.uuid)}
                              >
                                <ReceiptLongIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="ไม่มีสลิป">
                              <span>
                                <IconButton size="small" disabled>
                                  <ReceiptLongIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        {/* The branch's sign-off is independent of head office's: the
                            server lets the branch tick or untick whenever it gets to the
                            row, so the only thing left disabling the box is a route with
                            no transfer to verify yet.

                            What DOES freeze a deposit now is its branch-day being closed
                            (ปิดศูนย์เซล), and that state belongs to the branch-day rather
                            than the row — this table loads deposits per route and never
                            reads it. The stale rule is therefore dropped outright rather
                            than swapped for a guess: a box disabled on a rule this screen
                            cannot evaluate is a promise it cannot keep. The server refuses
                            the write on a closed day regardless. */}
                        <ReviewCheckboxCell
                          checked={Boolean(deposit?.bo_reviewed)}
                          reviewerName={deposit?.bo_reviewed_by_name ?? null}
                          reviewedAt={deposit?.bo_reviewed_at ?? null}
                          disabled={
                            !deposit ||
                            deposit.transfer_amount == null ||
                            reviewLoading[deposit.uuid]
                          }
                          onChange={changeDepositReviewed(deposit)}
                        />
                        <TableCell align="center">
                          <Button
                            size="small"
                            color="inherit"
                            disabled={!deposit}
                            onClick={() => deposit && setNoteTarget(deposit)}
                            sx={{
                              color: "link.main",
                              textDecoration: "underline",
                              fontWeight: deposit?.note ? 700 : 400,
                              "&:hover": {
                                textDecoration: "underline",
                                bgcolor: "transparent",
                              },
                              "&.Mui-disabled": {
                                color: "text.disabled",
                                textDecoration: "none",
                              },
                            }}
                          >
                            หมายเหตุ
                          </Button>
                        </TableCell>
                      </>
                    )}
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: STATUS_COLOR[waste.status] + "1A",
                          color: STATUS_COLOR[waste.status],
                          borderRadius: 4,
                          px: 2,
                          py: 0.5,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            flexShrink: 0,
                            bgcolor: STATUS_COLOR[waste.status],
                            borderRadius: "50%",
                            mr: 1,
                          }}
                        />
                        {STATUS_LABEL[waste.status]}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {!isHeadOfficeAccounting && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          disabled={
                            // `loading` covers the in-flight create: the overlay
                            // swallows mouse clicks but not a keyboard activation
                            // on the already-focused button.
                            loading ||
                            checkIsDisabled(waste.status, waste.route_code)
                          }
                          onClick={() => handleCreateReport(waste.route_code)}
                        >
                          สร้างรายงาน
                        </Button>
                      )}
                    </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <RouteLiveStockModal
        open={stockTarget !== null}
        routeUuid={stockTarget?.route_uuid ?? null}
        routeCode={stockTarget?.route_code ?? ""}
        date={selectedDate?.format("YYYY-MM-DD") ?? ""}
        onClose={() => setStockTarget(null)}
      />
      <TransferSlipsModal
        open={slipTarget !== null}
        slips={slipItems}
        depositUuid={slipTarget?.uuid}
        // Correcting is a finance/accounting action. The server's only other lock is the
        // branch-day being closed, which this table never loads, so the pencil follows the
        // role alone and a closed day is refused server-side.
        canEditAmount={canEditSlipAmount}
        onAmountUpdated={async () => {
          if (selectedDate) await loadRouteData(selectedDate);
        }}
        onClose={() => setSlipTargetUuid(null)}
      />
      <TransferNotificationModal
        open={Boolean(notificationTarget)}
        target={notificationTarget}
        onClose={() => setNotificationTarget(null)}
        onSaved={async () => {
          // Refetch the deposits (the record just created) — the deposit columns no
          // longer read through the sale report, so that is what has to be reloaded.
          if (selectedDate) await loadRouteData(selectedDate);
        }}
      />
      <DepositNoteModal
        open={Boolean(noteTarget)}
        note={noteTarget?.note}
        loading={noteSaving}
        onClose={() => setNoteTarget(null)}
        onSave={handleSaveNote}
      />
    </Box>
  );
}
